import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { useQuery } from "@apollo/client";
import { PanelForm } from "./PanelForm";
import { GET_EQUIPMENT } from "../../../api";
import { ApiContainer } from "../APIContainer";
import { useSaleMode } from "../../../context";
import { PanelFormStyled } from "./PanelFormStyled";
import { PanelFormActions } from "./PanelFormActions";
import { EditProposalAdapter } from "../EditProposalAdapter";
import { deskingActions, deskingSelectors } from "../../../model";
import { EquipmentsResponse, EquipmentsVariables } from "../../../api/types";
import { getCcEmailPreSelectedValue } from "../../../lib/convertVOToInitialFormValue";
import { convertInvoiceToSaleDetailsFormValues, getFormDefaultValues } from "../../../lib";
import {
  CcEmailOption,
  ContactForm,
  Equipment,
  FinanceProgramFormValues,
  PanelFormContainerProps,
  SaleType,
  SubmitOptions,
} from "../../../types";

export const PanelFormContainer = (props: PanelFormContainerProps) => {
  const {
    actions,
    accountId,
    userEmail,
    vo = null,
    invoice = null,
    proposalId = null,
    creditApplication,
    onSubmitDesking,
    invoiceAction,
    customSections,
    isProposalLoading,
    allAvailableCcEmailOptions = [],
    isSupportSaleDetailsUpdating = false,
  } = props;

  const { onChangeSaleType } = useSaleMode();

  const dispatch = useDispatch();
  const builder = useSelector(deskingSelectors.builder);
  const financePrising = useSelector(deskingSelectors.financePrising);
  const internalProposalIdForEdit = useSelector(deskingSelectors.proposalIdForEdit);
  const commonProductsConfiguration = useSelector(deskingSelectors.commonProductsConfiguration);
  const productsConfigInMenu = useSelector(deskingSelectors.proposalProductConfigurationsInMenu);
  const lenderProfiles = useSelector((state: any) => state.lp);

  const { data: equipmentData, refetch: refetchEquipmentData } = useQuery<
    EquipmentsResponse<Equipment>,
    Partial<EquipmentsVariables>
  >(GET_EQUIPMENT, {
    skip: !vo?.vendorOpportunityId,
    notifyOnNetworkStatusChange: true,
    variables: { VOId: vo?.vendorOpportunityId },
    onCompleted(response) {
      if (!Array.isArray(response?.equipments)) return;
      dispatch(deskingActions.setEquipmentData({ equipments: response.equipments }));
    },
  });

  const formDefaultValues = getFormDefaultValues(
    vo,
    creditApplication,
    equipmentData?.equipments ?? [],
    allAvailableCcEmailOptions,
    userEmail
  );

  const form = useForm<FinanceProgramFormValues>({
    defaultValues: formDefaultValues,
  });

  const ccEmailOptionsToSet = allAvailableCcEmailOptions.filter(
    ({ email }) => email !== formDefaultValues.contact.email
  );

  const [ccEmailOptions, setCcEmailOptions] = useState<CcEmailOption[]>(ccEmailOptionsToSet);

  useEffect(() => {
    if (!invoice) return;

    const result = convertInvoiceToSaleDetailsFormValues(invoice);
    Object.entries(result).forEach(([key, value]) => form.setValue(`saleDetails.${key}` as any, value));
  }, [form, invoice]);

  const handleResetDesking = () => {
    form.reset(formDefaultValues);
    onChangeSaleType(SaleType.Financing);
    dispatch(deskingActions.resetStructureAndMenuOptions());
  };

  const handleSubmitForm = async ({ isDraft }: SubmitOptions) => {
    await onSubmitDesking({
      builder,
      isDraft,
      productsConfigInMenu,
      form: form.getValues(),
      pricing: financePrising,
      commonProductsConfiguration,
      onDeskingReset: handleResetDesking,
    });
  };

  return (
    <PanelFormStyled>
      <FormProvider {...form}>
        <ApiContainer accountId={accountId} location={creditApplication?.location} lenderProfiles={lenderProfiles}>
          {({ getEquipmentDataById, refetchProposalProducts }) => (
            <EditProposalAdapter proposalId={internalProposalIdForEdit || proposalId}>
              <PanelForm
                vo={vo}
                accountId={accountId}
                invoiceAction={invoiceAction}
                customSections={customSections}
                ccEmailOptions={ccEmailOptions}
                formDefaultValues={formDefaultValues}
                refetchProposalProducts={refetchProposalProducts}
                isSupportSaleDetailsUpdating={isSupportSaleDetailsUpdating}
                actions={{
                  onEquipmentCreated: async equipmentId => {
                    await refetchEquipmentData();
                    if (actions?.onEquipmentCreated) await actions.onEquipmentCreated(equipmentId);

                    if (!vo?.vendorOpportunityId) return;

                    await getEquipmentDataById({ variables: { VOId: vo.vendorOpportunityId, id: equipmentId } });
                  },
                  onEquipmentUpdated: async equipmentId => {
                    await refetchEquipmentData();
                    if (actions?.onEquipmentUpdated) await actions.onEquipmentUpdated(equipmentId);
                  },
                  onSyncEquipment: async () => {
                    const { data } = await refetchEquipmentData();

                    dispatch(deskingActions.setEquipmentData({ equipments: data?.equipments }));

                    const id = data?.equipments?.[0]?.equipmentId;

                    if (!id || !vo?.vendorOpportunityId) return;

                    await getEquipmentDataById({ variables: { VOId: vo.vendorOpportunityId, id } });
                  },
                  onContactChanged: (customer: ContactForm) => {
                    const ccEmailPreSelectedValue = getCcEmailPreSelectedValue(
                      allAvailableCcEmailOptions,
                      customer,
                      vo,
                      userEmail
                    );
                    const ccEmailOptionsToSet = allAvailableCcEmailOptions.filter(o => o.email !== customer.email);
                    form.setValue("ccEmail", ccEmailPreSelectedValue);
                    setCcEmailOptions(ccEmailOptionsToSet);
                  },
                }}
              />

              <PanelFormActions onSubmitForm={handleSubmitForm} isProposalLoading={isProposalLoading} />
            </EditProposalAdapter>
          )}
        </ApiContainer>
      </FormProvider>
    </PanelFormStyled>
  );
};
